const DAY_MS = 24 * 60 * 60 * 1000;

function normalizeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isPositiveNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** Sum configured lbs/day across feeders (active outlook uses pre-filtered targets). */
function sumFeedTargetConsumptionLbsPerDay(feedTargets = []) {
  if (!Array.isArray(feedTargets)) return 0;
  return feedTargets.reduce(
    (sum, target) => sum + Math.max(0, Number(target?.consumptionRateLbsPerDay) || 0),
    0
  );
}

function aggregateDailyAdds(events = []) {
  const totals = new Map();
  events.forEach((event) => {
    const normalizedDate = normalizeDate(event?.date || event?.recorded_at);
    if (!normalizedDate) return;
    const amount = Number(event?.amount_lbs);
    if (!isPositiveNumber(amount)) return;
    const current = totals.get(normalizedDate) || 0;
    totals.set(normalizedDate, current + amount);
  });
  return Array.from(totals.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([date, total]) => ({ date, total }));
}

function calculateAverageConsumption(dailyAdds = [], { windowDays = 30 } = {}) {
  if (!Array.isArray(dailyAdds) || dailyAdds.length === 0) return 0;
  const sorted = [...dailyAdds].sort((a, b) => (a.date < b.date ? -1 : 1));
  const lastDate = new Date(sorted[sorted.length - 1].date);
  if (Number.isNaN(lastDate.getTime())) return 0;
  const windowStartMs = lastDate.getTime() - ((Math.max(1, windowDays) - 1) * DAY_MS);
  const windowed = sorted.filter((entry) => {
    const entryDate = new Date(entry.date);
    return entryDate.getTime() >= windowStartMs;
  });
  if (windowed.length === 0) return 0;
  const earliest = new Date(windowed[0].date);
  const daySpan = Math.max(1, Math.round((lastDate - earliest) / DAY_MS) + 1);
  const total = windowed.reduce((sum, entry) => sum + entry.total, 0);
  return total / daySpan;
}

function estimateDaysUntilEmpty({ estimatedRemainingLbs = 0, avgDailyUse = 0 }) {
  if (!isPositiveNumber(estimatedRemainingLbs) || !isPositiveNumber(avgDailyUse)) return null;
  return Math.max(0, Math.round((estimatedRemainingLbs / avgDailyUse) * 10) / 10);
}

function projectFeedNeed({ avgDailyUse = 0, days = 0, safetyFactor = 1 }) {
  if (!isPositiveNumber(avgDailyUse) || !isPositiveNumber(days)) return 0;
  const projected = avgDailyUse * days * (safetyFactor > 0 ? safetyFactor : 1);
  return Math.round(projected);
}

function mean(values) {
  if (!values.length) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

function standardDeviation(values) {
  if (values.length < 2) return 0;
  const avg = mean(values);
  const variance = values.reduce((sum, val) => sum + ((val - avg) ** 2), 0) / (values.length - 1);
  return Math.sqrt(variance);
}

function pearsonCorrelation(xs, ys) {
  if (xs.length !== ys.length || xs.length < 2) return 0;
  const xMean = mean(xs);
  const yMean = mean(ys);
  const numerator = xs.reduce((sum, x, idx) => sum + ((x - xMean) * (ys[idx] - yMean)), 0);
  const xStd = standardDeviation(xs);
  const yStd = standardDeviation(ys);
  if (xStd === 0 || yStd === 0) return 0;
  return numerator / ((xs.length - 1) * xStd * yStd);
}

function applyDeerActivityAdjustment({
  avgDailyUse = 0,
  dailyAdds = [],
  deerSightings = [],
  minCorrelation = 0.2,
  maxBoost = 0.25
}) {
  if (!isPositiveNumber(avgDailyUse) || !Array.isArray(deerSightings) || deerSightings.length === 0) {
    return avgDailyUse;
  }
  const addMap = new Map(dailyAdds.map((entry) => [entry.date, entry.total]));
  const overlapping = deerSightings
    .map((sighting) => {
      const date = normalizeDate(sighting.date);
      if (!date) return null;
      const feedTotal = addMap.get(date);
      if (!isPositiveNumber(feedTotal)) return null;
      const deerCount = Number(sighting.count);
      if (!isPositiveNumber(deerCount)) return null;
      return { feedTotal, deerCount };
    })
    .filter(Boolean);
  if (overlapping.length < 2) return avgDailyUse;
  const feedValues = overlapping.map((item) => item.feedTotal);
  const deerValues = overlapping.map((item) => item.deerCount);
  const correlation = pearsonCorrelation(feedValues, deerValues);
  if (correlation <= minCorrelation) return avgDailyUse;
  const boost = Math.min(maxBoost, correlation - minCorrelation);
  return Math.round(avgDailyUse * (1 + boost) * 100) / 100;
}

function buildConsumptionSummary({
  feedTargets = [],
  events = [],
  deerSightings = [],
  estimatedRemainingLbs = 0,
  projectionDays = [7, 30],
  windowDays = 30
}) {
  const dailyAdds = aggregateDailyAdds(events);
  const configuredDailyUse = sumFeedTargetConsumptionLbsPerDay(feedTargets);

  let baseAvgDailyUse = configuredDailyUse;
  let avgDailyUse = configuredDailyUse;

  if (!isPositiveNumber(configuredDailyUse)) {
    baseAvgDailyUse = calculateAverageConsumption(dailyAdds, { windowDays });
    avgDailyUse = applyDeerActivityAdjustment({
      avgDailyUse: baseAvgDailyUse,
      dailyAdds,
      deerSightings
    });
  }

  const projectedNeeds = {};
  (Array.isArray(projectionDays) ? projectionDays : []).forEach((days) => {
    if (!Number.isFinite(days)) return;
    projectedNeeds[days] = projectFeedNeed({ avgDailyUse, days });
  });
  return {
    dailyAdds,
    baseAvgDailyUse,
    avgDailyUse,
    daysUntilEmpty: estimateDaysUntilEmpty({
      estimatedRemainingLbs,
      avgDailyUse
    }),
    projectedNeeds
  };
}

export {
  aggregateDailyAdds,
  calculateAverageConsumption,
  sumFeedTargetConsumptionLbsPerDay,
  estimateDaysUntilEmpty,
  projectFeedNeed,
  applyDeerActivityAdjustment,
  buildConsumptionSummary
};

const feedConsumptionService = {
  aggregateDailyAdds,
  calculateAverageConsumption,
  sumFeedTargetConsumptionLbsPerDay,
  estimateDaysUntilEmpty,
  projectFeedNeed,
  applyDeerActivityAdjustment,
  buildConsumptionSummary
};

export default feedConsumptionService;

